import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, ProgressBar } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';

import PathSynchroLogic, { AgentNotOnlineMarker } from '../../logics/pathsynchromodule/PathSynchroLogic';
import MessageForm, { MessageFormHandle, ErrorMessage } from '../message-form/MessageForm';

import './SynchroExecution.css';

interface SynchroExecutionProps {
    PathId: number;
    IsLaunchable: boolean;
    LastVersionId: number | null;
    PublishedVersionId: number | null;
}

const SynchroExecution: React.FC<SynchroExecutionProps> = ({ PathId, IsLaunchable, LastVersionId, PublishedVersionId }) => {

    const [syncToDo, setSyncToDo] = useState(false);
    const [progress, setProgress] = useState(0);
    const [syncDone, setSyncDone] = useState(false);

    const messageFormRef = useRef<MessageFormHandle>(null);

    // The synchronization always runs the published version: warn when the last saved version is not it.
    const lastVersionNotPublished = LastVersionId !== null && LastVersionId !== PublishedVersionId;

    const synchro = useCallback(async () => {
        if (syncToDo === true) {

            let result = await PathSynchroLogic.PathSynchroAsync(PathId, (progress) => { setProgress(progress); });

            if (result.IsFailed()) {
                // Launching a synchro by hand while the customer agent is down (same situation the
                // scheduler hits): show a dedicated message instead of a raw alert. Any other failure
                // falls back to a generic message.
                const isAgentOffline = (result.ErrorMessage ?? "").includes(AgentNotOnlineMarker);
                messageFormRef.current?.Open(
                    formatMessage(isAgentOffline ? "SynchroExecution_AgentNotAvailable" : "SynchroExecution_LaunchError"),
                    ErrorMessage);
            }

            setSyncDone(true);
            setSyncToDo(false);
        }
    }, [PathId, syncToDo]);

    useEffect(() => {
        synchro()
    }, [synchro]);

    const synchroAction = async () => {
        setSyncDone(false);
        setProgress(0);
        setSyncToDo(true);
    }

    return (
        <div className='BlockCard'>
            <div className='BlockHeader'>
                <Button onClick={synchroAction} disabled={syncToDo || !IsLaunchable} type="default" stylingMode="contained">
                    Synchronize
                </Button>
                {lastVersionNotPublished && <div className='VersionWarning'>{formatMessage("SynchroExecution_DraftWarning")}</div>}
                <div className="ProgressContainer">
                    {(syncToDo || syncDone) && <ProgressBar color="primary" value={progress} width='100%' />}
                </div>
            </div>
            <MessageForm ref={messageFormRef} />
        </div>
    );
}

export default SynchroExecution;
